<script setup lang="ts">
import { Input } from '@/ui/input'
import { onClickOutside } from '@vueuse/core'
import EmailVariableCommandList from './EmailVariableCommandList.vue'
import { emailVariables, type EmailVariable } from '../constants/variables'
import { insertTextAtRange } from '../utils/insertAtCursor'

const props = defineProps<{
  modelValue: string
  maxlength?: number
  placeholder?: string
  ariaInvalid?: boolean
}>()

const emit = defineEmits<{
  (e: 'update:modelValue', value: string): void
  (e: 'focus'): void
}>()

const inputRef = ref<{ $el: HTMLInputElement } | null>(null)
const containerRef = ref<HTMLElement | null>(null)
const dropdownRef = ref<HTMLElement | null>(null)

const open = ref(false)
const activeIndex = ref(0)
const query = ref('')
const mentionStart = ref<number | null>(null)
const position = ref({ top: 0, left: 0 })

const items = computed<EmailVariable[]>(() => {
  if (!query.value) return emailVariables
  const q = query.value.toLowerCase()
  return emailVariables.filter(
    (v) =>
      v.key.toLowerCase().includes(q) || v.label.toLowerCase().includes(q) || v.description.toLowerCase().includes(q)
  )
})

const maxLength = computed(() => props.maxlength ?? Number.POSITIVE_INFINITY)

onClickOutside(containerRef, () => close(), { ignore: [dropdownRef] })

function inputEl(): HTMLInputElement | null {
  return inputRef.value?.$el ?? null
}

function close() {
  open.value = false
  mentionStart.value = null
}

function detectMention() {
  const input = inputEl()
  if (!input) return
  const caret = input.selectionStart ?? input.value.length
  const match = /(?:^|\s)@([\w-]*)$/.exec(input.value.slice(0, caret))

  if (!match) {
    close()
    return
  }

  query.value = match[1]
  mentionStart.value = caret - match[1].length - 1

  if (!open.value) {
    open.value = true
    activeIndex.value = 0
  }

  const rect = input.getBoundingClientRect()
  position.value = { top: rect.bottom + 4, left: rect.left }
}

function onInput(value: string | number) {
  emit('update:modelValue', String(value))
  nextTick(detectMention)
}

function onKeydown(event: KeyboardEvent) {
  if (!open.value) return

  if (event.key === 'ArrowDown') {
    event.preventDefault()
    move(1)
  } else if (event.key === 'ArrowUp') {
    event.preventDefault()
    move(-1)
  } else if (event.key === 'Enter' || event.key === 'Tab') {
    event.preventDefault()
    const variable = items.value[activeIndex.value]
    if (variable) applyVariable(variable)
  } else if (event.key === 'Escape') {
    event.preventDefault()
    close()
  }
}

function move(delta: number) {
  const count = items.value.length
  if (count === 0) return
  activeIndex.value = (activeIndex.value + delta + count) % count
}

function applyVariable(variable: EmailVariable) {
  const input = inputEl()
  if (!input || mentionStart.value === null) return

  const caret = input.selectionStart ?? input.value.length
  const { value, caret: newCaret } = insertTextAtRange(
    input.value,
    mentionStart.value,
    caret,
    `${variable.key} `,
    maxLength.value
  )

  emit('update:modelValue', value)
  close()

  nextTick(() => {
    input.focus()
    input.setSelectionRange(newCaret, newCaret)
  })
}

function insertVariable(text: string) {
  const input = inputEl()
  const current = input?.value ?? props.modelValue
  const start = input?.selectionStart ?? current.length
  const end = input?.selectionEnd ?? current.length
  const { value, caret } = insertTextAtRange(current, start, end, `${text} `, maxLength.value)

  emit('update:modelValue', value)

  nextTick(() => {
    if (!input) return
    input.focus()
    input.setSelectionRange(caret, caret)
  })
}

defineExpose({ insertVariable })
</script>

<template>
  <div ref="containerRef" class="flex-1">
    <Input
      ref="inputRef"
      :model-value="modelValue"
      :maxlength="maxlength"
      :aria-invalid="ariaInvalid || undefined"
      :placeholder="placeholder"
      @update:model-value="onInput"
      @keydown="onKeydown"
      @focus="emit('focus')"
      @click="detectMention"
      @keyup="detectMention"
    />

    <Teleport to="#email-variable-popups" defer>
      <div
        v-if="open"
        ref="dropdownRef"
        class="fixed z-[100]"
        :style="{ top: `${position.top}px`, left: `${position.left}px` }"
      >
        <EmailVariableCommandList
          :items="items"
          :active-index="activeIndex"
          @select="applyVariable"
          @hover="activeIndex = $event"
        />
      </div>
    </Teleport>
  </div>
</template>
